import DetailsCard from './detailsCard';
import { useContext, useEffect, useState } from 'react';
import { MPosContext } from 'utils/context';
import * as Styles from './styles';
import { ButtonWrapper } from '../vehicleDetails/styles';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { getDD_MM_YYYY, getYYYY_MM_DD } from 'utils/date';
import { uuid, getImageBasePath } from 'utils/generics';
import { useRouter } from 'next/router';
import { NewPlanStages } from '../constants';
import {
    SegmentEventPages,
    SegmentEventPageTypes,
    SegmentEventNameTypes,
    SegmentEventActionNames,
    SegmentEventActionTypes,
} from 'utils/segment/constants';
import sendSegmentEvent from 'utils/segment';

import SnackBar from 'library/snackbar';
import { isManualFlowEnabled } from '../utils';
import { REVIEW_DETAILS } from './constants';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';

const ReviewDetails = () => {
    const [renderData, setRenderData] = useState<any[]>([]);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const {
        planDetails,
        partnerId,
        setPlanDetails,
        setActiveStage,
        permissions,
        userDetails,
    } = useContext(MPosContext);
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const { isMobile } = useResponsiveDevice();

    // Segment Events
    useEffect(() => {
        sendSegmentEvent(
            SegmentEventNameTypes.VIEW,
            {
                page_type: SegmentEventPageTypes.REVIEW_DETAILS_PAGE,
                view_name: SegmentEventPages.VIEW_REVIEW_DETAILS_PAGE,
            },
            { userDetails, isMobile }
        );
    }, [window]);

    useEffect(() => {
        if (planDetails) {
            const { invoice_date } = planDetails.device;
            const rData: any = [];
            const vehicleDetails = {
                id: REVIEW_DETAILS.VEHICLE_DETAILS,
                src: getImageBasePath('/icons/vehicle.svg'),
                header: {
                    heading: 'Vehicle details',
                    link: () => {
                        setActiveStage(NewPlanStages.VEHICLE_DETAILS);
                    },
                },
                body: [
                    {
                        label: 'Vehicle model',
                        value: planDetails?.device?.name || '',
                    },
                    {
                        label: 'Date of vehicle Invoice',
                        value: getDD_MM_YYYY(new Date(invoice_date)).replace(
                            /-/g,
                            '/'
                        ),
                    },
                    { label: 'Vin', value: planDetails.device.vin_number },
                    { label: 'Bin', value: planDetails.device.bin_number },
                    {
                        label: 'Bin 2',
                        value: planDetails.device.bin2_number || 'NA',
                    },
                    {
                        label: 'Motor number',
                        value: planDetails.device.motor_number || 'NA',
                    },
                    {
                        label: 'Charger number',
                        value: planDetails.device.charger_number || 'NA',
                    },
                ],
            };
            if (planDetails?.device.registration_number) {
                vehicleDetails.body.push({
                    label: 'Registration Number',
                    value: planDetails.device.registration_number,
                });
            }
            const userDetails = {
                id: REVIEW_DETAILS.CUSTOMER_DETAILS,
                src: getImageBasePath('/icons/customer.svg'),
                header: {
                    heading: 'Customer details',
                    link: () => {
                        setActiveStage(NewPlanStages.USER_DETAILS);
                    },
                },
                body: [
                    { label: 'Full Name', value: planDetails.customer.name },
                    { label: 'Mobile No.', value: planDetails.customer.phone },
                    { label: 'Email ID', value: planDetails.customer.email },
                    { label: 'Address', value: planDetails.customer.address },
                    { label: 'Pin Code', value: planDetails.customer.pincode },
                ],
            };
            const pDetails = {
                id: REVIEW_DETAILS.PLAN_DETAILS,
                src: getImageBasePath('/icons/plan.svg'),
                header: {
                    heading: 'Plan details',
                    link: () => {
                        setActiveStage(NewPlanStages.PLAN);
                    },
                },
                body: [
                    {
                        label: planDetails.plan.plan_category,
                        value: `₹${planDetails.plan.premium}`,
                    },
                ],
            };
            rData.push(vehicleDetails);
            rData.push(userDetails);
            rData.push(pDetails);
            setRenderData(rData);
        }
    }, [planDetails]);

    const handleSubmit = () => {
        const sNo = uuid(16);
        // segment Events
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.ISSUE_POLICY,
            },
            { userDetails, isMobile }
        );
        import('../documentUpload/apis').then(async (req) => {
            setIsLoading(true);
            const orderDetails = {
                customer: {
                    phone: planDetails.customer.phone,
                    name: planDetails.customer.name,
                    email: planDetails.customer.email,
                    pincode: planDetails.customer.pincode,
                    address: planDetails.customer.address,
                },
                device: {
                    name: planDetails.device.name,
                    category: planDetails.device.type,
                    serial_number: planDetails.device.serial_number || sNo,
                    purchase_date: getYYYY_MM_DD(
                        new Date(planDetails.device?.invoice_date)
                    ),
                    delivery_date: getYYYY_MM_DD(new Date()),
                    price: planDetails.device.price || 100,
                    brand: planDetails.device.brand,
                    vin: planDetails.device.vin_number,
                    bin: planDetails.device.bin_number,
                    bin2: planDetails.device?.bin2_number ?? null,
                    registration_number:
                        planDetails.device?.registration_number,
                    manufacturer_warranty: {
                        years: Number(3),
                        months: 0,
                        days: 0,
                    },
                    rto_code: planDetails.device.rto_code,
                    motor_number: planDetails.device?.motor_number ?? '',
                    charger_number: planDetails.device?.charger_number ?? '',
                },
                document_urls: {
                    imei: sNo,
                    invoice: uuid(10),
                },
                plan: {
                    partner: partnerId,
                    plan_id: planDetails.plan.plan_id,
                    plan_name: planDetails.plan.plan_name,
                    premium: planDetails.plan.premium,
                    promo_name: planDetails.plan.promo_name,
                    insurance_program_id: planDetails.plan.program_id,
                    insurance_order_id: uuid(10),
                    insurance_program_name: 'ACKO INSURANCE',
                    plan_purchase_date: getYYYY_MM_DD(new Date()),
                    plan_category: planDetails.plan.plan_category,
                    masterpolicy_number: planDetails.plan.program_id,
                    ...(planDetails.plan.product_code && {
                        product_code: planDetails.plan.product_code,
                    }),
                },
                order_communication: false,
                policy_communication: true,
            };
            const { status, data, statusCode, message } =
                await req.postOrderDetails(
                    orderDetails,
                    partnerId,
                    permissions.group
                );
            if (statusCode === 401) {
                router.push('/login');
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            setIsLoading(false);
            if (statusCode === 409) {
                setSnackbar({
                    type: 'failure',
                    message:
                        message || 'Policy already exists for this BIN number',
                });
                return;
            }
            if (status && data) {
                const { order_ekey } = data;
                setPlanDetails({
                    ...planDetails,
                    order_ekey,
                });
                setActiveStage(NewPlanStages.CUSTOMER_LINK);
                return;
            }
            setSnackbar({
                type: 'failure',
                message: message || 'Oops. Something went wrong.',
            });
        });
    };

    const isManualFlow = isManualFlowEnabled(
        permissions?.purchase_journey,
        planDetails?.showManualFlow
    );

    return (
        <Styles.PageWrapper>
            <Styles.PageHeading>Review details</Styles.PageHeading>
            {renderData.map((item) => {
                const showEditInfo =
                    item.id === REVIEW_DETAILS.PLAN_DETAILS || isManualFlow;
                return (
                    <div
                        key={item.header.heading}
                        style={{ marginBottom: '25px' }}
                    >
                        <DetailsCard
                            data={item}
                            showEditInfo={showEditInfo}
                            userDetails={userDetails}
                            isMobile={isMobile}
                        />
                    </div>
                );
            })}
            <ButtonWrapper>
                <PrimaryButton loading={isLoading} onClick={handleSubmit}>
                    Issue Plan
                </PrimaryButton>
            </ButtonWrapper>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </Styles.PageWrapper>
    );
};

export default ReviewDetails;
